import ContactType from "enums/ContactType";
import moment from "moment";
import { formatCashFull } from "utils/formatCash";
import PaymentReceivedFromType from "enums/PaymentReceivedFromType";
import PaymentPaymentTypes from "enums/PaymentPaymentTypes";
import PaymentStatus from "enums/PaymentStatus";
import { LocalImageAsset } from "hooks/useAttachmentAndAssetHandling";
import {
  CustomFieldsRecord,
  FormikCustomField,
} from "hooks/customFields.types";
import type { ExpenseCategory } from "components/common/CategorySearchAutoComplete";

export type FormikPayment = {
  id: string;
  dueDate?: Date | null;
  scheduledPaymentDate?: Date | null;
  actualPaymentDate?: Date | null;
  payTo: {
    id: string;
    name: string;
    email: string;
    type: ContactType | undefined;
    hasPaymentAccount: boolean;
  };
  amount: number;
  currency: string;
  receivedFrom?: {
    id: string;
    name?: string;
    email?: string;
    type: PaymentReceivedFromType | undefined;
  };
  description?: string;
  createdBy?: {
    id: string;
  };
  payFrom?: {
    last4: string;
    name: string;
  };
  paymentMethod?: PaymentPaymentTypes | undefined;
  status: PaymentStatus | undefined;
  notes?: string;
  attachments: LocalImageAsset[];
  linkedTransaction: {
    id: string;
    type: string;
    amount: number;
    currency: string;
  } | null;
  customFields?: CustomFieldsRecord;
  // NEW
  expenseCategory?: ExpenseCategory | undefined;
  allocationCode?:
    | {
        id: string;
        allocationCode: string;
      }
    | undefined;
  allocationType?:
    | {
        key: string;
        name: string;
      }
    | undefined;
};

export const paymentInitialValues = {
  id: "",
  dueDate: null,
  scheduledPaymentDate: null,
  actualPaymentDate: null,
  payTo: {
    id: "",
    name: "",
    email: "",
    type: undefined,
    hasPaymentAccount: false,
  },
  amount: 0,
  currency: "USD",
  receivedFrom: { id: "", name: "", email: "", type: undefined },
  description: "",
  payFrom: { last4: "", name: "" },
  paymentMethod: PaymentPaymentTypes.ACH,
  status: undefined,
  notes: "",
  attachments: [],
  linkedTransaction: null,
  customFields: {},
  expenseCategory: undefined,
  allocationCode: undefined,
  allocationType: undefined,
};

// For Formik
export function formatPaymentForFormik(
  payment: any,
  transformCustomFieldsToRecord?: (
    customFields: any,
    customFieldSettings?: FormikCustomField[],
  ) => CustomFieldsRecord,
  customFieldSettings?: FormikCustomField[],
): FormikPayment {
  const transformedPayment = {
    id: payment.id,
    dueDate: payment.dueDate ? new Date(Number(payment.dueDate)) : null,
    scheduledPaymentDate: payment.scheduledPaymentDate
      ? new Date(Number(payment.scheduledPaymentDate))
      : null,
    actualPaymentDate: payment.actualPaymentDate
      ? new Date(Number(payment.actualPaymentDate))
      : null,
    payTo: formatPayToForPayment(payment.payTo),
    amount: Math.abs(payment?.amount) || 0,
    currency: payment.currency || "USD",
    receivedFrom: payment.receivedFrom || {
      id: "",
      name: "",
      email: "",
      type: undefined,
    },
    description: payment.description || "",
    payFrom: payment.payFrom || { last4: "", name: "" },
    createdBy: payment.createdBy || { id: "" },
    paymentMethod: payment.paymentMethod || PaymentPaymentTypes.ACH,
    status: payment.status,
    notes: payment.notes || "",
    attachments: payment.attachments || [],
    linkedTransaction: payment.transaction || null,
    customFields: transformCustomFieldsToRecord
      ? transformCustomFieldsToRecord(payment.customFields, customFieldSettings)
      : {},
    expenseCategory: payment.expenseCategory
      ? {
          id: payment.expenseCategory.id,
          name: payment.expenseCategory.name,
          level: payment.expenseCategory.level,
        }
      : undefined,
    allocationCode: payment.allocationCode
      ? {
          id: payment.allocationCode.id,
          allocationCode: payment.allocationCode.allocationCode,
        }
      : undefined,
    allocationType: payment.allocationType
      ? {
          key: payment.allocationType.key,
          name: payment.allocationType.name,
        }
      : undefined,
  };

  return transformedPayment;
}

// For Table (dates as formatted strings)
export function formatPaymentForTable(payment: any) {
  return {
    id: payment.id,
    receivedFromName: payment.receivedFrom?.name || "",
    receivedFromEmail: payment.receivedFrom?.email || "",
    description: payment.description || "",
    dueDate: payment.dueDate
      ? moment(Number(payment.dueDate)).format("MMM D, YYYY")
      : "",
    scheduledPaymentDate: payment.scheduledPaymentDate
      ? moment(Number(payment.scheduledPaymentDate)).format("MMM D, YYYY")
      : "",
    actualPaymentDate: payment.actualPaymentDate
      ? moment(Number(payment.actualPaymentDate)).format("MMM D, YYYY")
      : "",
    payTo: formatPayToForPayment(payment.payTo)?.name || "",
    amount: formatCashFull(payment.amount, payment.currency || "USD"),
    currency: payment.currency || "USD",
    payFrom: payment.payFrom?.name || "",
    paymentMethod: payment.paymentMethod,
    status: payment.status,
    workflowRequestStatus: payment.workflowRequest?.status,
  };
}

export function formatPayToForPayment(payTo: any) {
  if (!payTo) {
    return {
      id: "",
      name: "",
      email: "",
      type: undefined,
      hasPaymentAccount: false,
    };
  }
  const name =
    payTo.contactType === ContactType.Business
      ? payTo.businessName || ""
      : [payTo.firstName, payTo.lastName].filter(Boolean).join(" ");
  return {
    id: payTo.id,
    name,
    email: payTo.email,
    type: payTo.contactType,
    hasPaymentAccount: !!payTo.hasPaymentAccount || !!payTo.paymentBankInfo,
  };
}

export function buildSavePaymentInput(
  values: FormikPayment,
  transformCustomFieldsToArray: (customFields?: CustomFieldsRecord) => any[],
) {
  return {
    dueDate: values.dueDate ? new Date(values.dueDate).getTime() : null,
    scheduledPaymentDate: values.scheduledPaymentDate
      ? new Date(values.scheduledPaymentDate).getTime()
      : null,
    payToId: values.payTo?.id,
    amount: values.amount,
    receivedFrom: values.receivedFrom?.id
      ? {
          id: values.receivedFrom.id,
          type: values.receivedFrom.type,
          name: values.receivedFrom.name,
          email: values.receivedFrom.email,
        }
      : undefined,
    status: values.status,
    paymentMethod: values.paymentMethod,
    customFields: transformCustomFieldsToArray(values.customFields),
    expenseCategoryId: values.expenseCategory?.id ?? null,
    allocationCodeId: values.allocationCode?.id ?? null,
  };
}

export function canEditPayment(paymentStatus?: PaymentStatus) {
  if (!paymentStatus) return false;
  const editableStatuses = [
    PaymentStatus.DRAFT,
    PaymentStatus.PENDING,
    PaymentStatus.PENDING_PAYMENT,
  ];
  return editableStatuses.includes(paymentStatus);
}

export function canResubmitPayment(
  paymentStatus?: PaymentStatus,
  actualPaymentDate?: Date | number | string | null,
) {
  if (!paymentStatus) return false;
  // Funds already left; the same record can never be re-sent, only re-used as a new payment.
  if (actualPaymentDate) return false;
  const resubmittableStatuses = [
    PaymentStatus.PAYMENT_FAILED,
    PaymentStatus.INSUFFICIENT_FUNDS,
    PaymentStatus.CANCELED,
  ];
  return resubmittableStatuses.includes(paymentStatus);
}

export function canCancelPayment(paymentStatus?: PaymentStatus) {
  if (!paymentStatus) return false;
  const cancelableStatuses = [
    PaymentStatus.DRAFT,
    PaymentStatus.PENDING,
    PaymentStatus.PENDING_PAYMENT,
    PaymentStatus.PAYMENT_FAILED,
    PaymentStatus.INSUFFICIENT_FUNDS,
  ];
  return cancelableStatuses.includes(paymentStatus);
}

export function canMarkAsPaid(paymentStatus?: PaymentStatus) {
  if (!paymentStatus) return false;
  // RETURNED stays markable: funds bounced back but may be settled out of band, and
  // mark-as-paid only reconciles (no money moves). Mirrors the markAsPaid resolver.
  return (
    paymentStatus !== PaymentStatus.PROCESSING_PAYMENT &&
    paymentStatus !== PaymentStatus.COMPLETE
  );
}

export function canPayNow(paymentStatus?: PaymentStatus) {
  if (!paymentStatus) return false;
  return paymentStatus === PaymentStatus.PENDING_PAYMENT;
}

export function canSavePaymentAsDraft(
  paymentId: string,
  paymentStatus?: PaymentStatus,
) {
  if (!paymentStatus) return false;
  return (
    !paymentId ||
    canEditPayment(paymentStatus) ||
    canResubmitPayment(paymentStatus)
  );
}

export function canSavePayment(
  paymentId: string,
  paymentStatus?: PaymentStatus,
) {
  if (!paymentStatus) return false;
  return (
    !paymentId ||
    canEditPayment(paymentStatus) ||
    canResubmitPayment(paymentStatus)
  );
}
